/**
 * RenderService - Singleton service for managing photo renders
 * 
 * Handles:
 * - Caching rendered images in IndexedDB (50MB limit, 100 item limit, LRU)
 * - Streaming renders from server with progress updates
 * - Multiplexing requests (multiple listeners for same photo+settings)
 * - Automatic cleanup when all listeners cancel
 */

class RenderService {
  constructor() {
    this.db = null;
    this.dbName = 'RenderCache';
    this.dbVersion = 1;
    this.storeName = 'renders';
    
    // Active renders: Map<cacheKey, { abortController, listeners: Map<listenerId, callbacks> }>
    this.activeRenders = new Map();
    
    // Next listener ID
    this.nextListenerId = 1;
    
    // Cache limits
    this.maxItems = 100;
    this.maxBytes = 50 * 1024 * 1024; // 50MB
    
    // Streaming service URL (detect environment)
    this.streamingServiceUrl = this.getStreamingServiceUrl();
    
    // Initialize DB (store promise to reuse)
    this.dbReady = this.initDB();
  }

  /**
   * Get the streaming service URL based on environment
   */
  getStreamingServiceUrl() {
    // In development (localhost), use localhost:10001
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:10001';
    }
    // In production, use same hostname with port 10001
    return `https://${window.location.hostname}:10001`;
  }

  /**
   * Initialize IndexedDB
   */
  async initDB() {
    if (this.db) return this.db;
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object store with cacheKey as key
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'cacheKey' });
          store.createIndex('lastAccessed', 'lastAccessed', { unique: false });
        }
      };
    });
  }

  /**
   * Generate cache key from photo ID and settings
   */
  getCacheKey(photoId, settings) {
    // Sort settings keys for consistent cache keys
    const sortedSettings = Object.keys(settings).sort().reduce((acc, key) => {
      acc[key] = settings[key];
      return acc;
    }, {});
    const key = `${photoId}_${JSON.stringify(sortedSettings)}`;
    return key;
  }

  /**
   * Get a rendered photo (from cache or by fetching)
   * 
   * @param {number} photoId - Photo ID
   * @param {object} settings - Render settings (all parameters)
   * @param {object} callbacks - { onProgress, onError, onComplete }
   * @returns {function} cancel function
   */
  getPhoto(photoId, settings, { onProgress, onError, onComplete }) {
    const cacheKey = this.getCacheKey(photoId, settings);
    const listenerId = this.nextListenerId++;
    
    // Check if render is already in progress with the SAME settings
    let renderState = this.activeRenders.get(cacheKey);
    
    if (renderState) {
      // Render with these exact settings is already in progress
      // Just add this listener to get progress updates
      renderState.listeners.set(listenerId, { onProgress, onError, onComplete });
      
      // Return cancel function immediately - don't restart render
      return () => {
        this.cancelListener(cacheKey, listenerId);
      };
    } else {
      // Create and register renderState IMMEDIATELY to prevent race conditions
      // This ensures subsequent calls see the active render and add themselves as listeners
      renderState = {
        abortController: new AbortController(),
        listeners: new Map([[listenerId, { onProgress, onError, onComplete }]])
      };
      this.activeRenders.set(cacheKey, renderState);
      
      // Now try to get from cache - if complete, serve it and clean up
      this.getFromCache(cacheKey).then(cached => {
        if (cached && cached.isComplete) {
          // Serve cached data to all listeners
          renderState.listeners.forEach(callbacks => {
            if (callbacks.onProgress) {
              callbacks.onProgress(cached.imageData);
            }
            if (callbacks.onComplete) {
              callbacks.onComplete(cached.imageData);
            }
          });
          
          // Update last accessed time
          this.updateLastAccessed(cacheKey);
          
          // Clean up renderState since we're serving from cache
          this.activeRenders.delete(cacheKey);
        } else {
          // Cache miss or incomplete - start new render
          if (cached) {
            // Serve partial cached data first to all listeners
            renderState.listeners.forEach(callbacks => {
              if (callbacks.onProgress) {
                callbacks.onProgress(cached.imageData);
              }
            });
          }
          
          // Start fetching (renderState already registered)
          this.startRender(photoId, settings, cacheKey, renderState);
        }
      }).catch(error => {
        // On cache error, start render anyway (renderState already registered)
        console.error('Cache lookup error:', error);
        this.startRender(photoId, settings, cacheKey, renderState);
      });
    }
    
    // Return cancel function
    return () => {
      this.cancelListener(cacheKey, listenerId);
    };
  }

  /**
   * Start rendering a photo
   */
  async startRender(photoId, settings, cacheKey, renderState) {
    const startTime = performance.now();
    console.log('[RenderService] Starting render:', {
      photoId,
      cacheKey,
      settings,
      streamingServiceUrl: this.streamingServiceUrl
    });
    
    try {
      // Send settings as JSON body (no authentication needed)
      const response = await fetch(`${this.streamingServiceUrl}/generate/${photoId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings),
        signal: renderState.abortController.signal
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
      
      // Stream and parse length-prefixed images
      await this.parseStreamingResponse(response, cacheKey, renderState, startTime, settings);
      
      // Cleanup
      this.activeRenders.delete(cacheKey);
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('[RenderService] Render cancelled (AbortError):', cacheKey);
        // Remove from cache if aborted before completion
        await this.removeFromCache(cacheKey);
      } else {
        console.error('[RenderService] Render error:', error);
        // Notify all listeners of error
        renderState.listeners.forEach(callbacks => {
          if (callbacks.onError) {
            callbacks.onError(error);
          }
        });
      }
      
      // Cleanup
      this.activeRenders.delete(cacheKey);
    }
  }

  /**
   * Parse streaming response and notify listeners
   */
  async parseStreamingResponse(response, cacheKey, renderState, startTime, settings) {
    const reader = response.body.getReader();
    let buffer = new Uint8Array(0);
    let lastImageData = null;
    let firstImageReceived = false;
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }
      
      // Append new data to buffer
      const newBuffer = new Uint8Array(buffer.length + value.length);
      newBuffer.set(buffer);
      newBuffer.set(value, buffer.length);
      buffer = newBuffer;
      
      // Parse length-prefixed images from buffer
      while (buffer.length >= 4) {
        // Read 4-byte big-endian length
        const length = (buffer[0] << 24) | (buffer[1] << 16) | (buffer[2] << 8) | buffer[3];
        
        // Check if we have the full image
        if (buffer.length < 4 + length) {
          break; // Wait for more data
        }
        
        // Extract image data
        const jpegData = buffer.slice(4, 4 + length);
        buffer = buffer.slice(4 + length);
        lastImageData = jpegData;
        
        // Log time to first image
        if (!firstImageReceived) {
          const timeToFirstImage = performance.now() - startTime;
          console.log('[RenderService] First image received:', {
            timeMs: timeToFirstImage.toFixed(2),
            settings: settings,
            cacheKey: cacheKey.substring(0, 50) + '...'
          });
          firstImageReceived = true;
        }
        
        // Store in cache as incomplete (replaces previous version)
        await this.storeInCache(cacheKey, jpegData, false);
        
        // Notify all listeners
        renderState.listeners.forEach(callbacks => {
          if (callbacks.onProgress) {
            callbacks.onProgress(jpegData);
          }
        });
      }
    }
    
    // Mark final image as complete in cache
    if (lastImageData) {
      await this.storeInCache(cacheKey, lastImageData, true);
    }
    
    // Notify completion
    renderState.listeners.forEach(callbacks => {
      if (callbacks.onComplete && lastImageData) {
        callbacks.onComplete(lastImageData);
      }
    });
  }

  /**
   * Cancel a specific listener
   */
  cancelListener(cacheKey, listenerId) {
    const renderState = this.activeRenders.get(cacheKey);
    if (!renderState) return;
    
    console.log(`[RenderService] Canceling listener ${listenerId} for cache key:`, cacheKey);
    
    // Remove this listener
    renderState.listeners.delete(listenerId);
    
    // If no listeners left, abort the render
    if (renderState.listeners.size === 0) {
      console.log(`[RenderService] No listeners remaining - aborting render for cache key:`, cacheKey);
      renderState.abortController.abort();
      this.activeRenders.delete(cacheKey);
    }
  }

  /**
   * Store image in cache
   */
  async storeInCache(cacheKey, imageData, isComplete = false) {
    await this.dbReady;
    
    const entry = {
      cacheKey,
      imageData,
      byteSize: imageData.byteLength,
      isComplete,
      lastAccessed: Date.now()
    };
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.put(entry);
      request.onsuccess = () => {
        // Enforce cache limits
        this.enforceCacheLimits();
        resolve();
      };
      request.onerror = () => {
        console.error(`[storeInCache] Error storing ${cacheKey.substring(0, 30)}...:`, request.error);
        reject(request.error);
      };
    });
  }

  /**
   * Get image from cache
   */
  async getFromCache(cacheKey) {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Remove from cache
   */
  async removeFromCache(cacheKey) {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.delete(cacheKey);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update last accessed time
   */
  async updateLastAccessed(cacheKey) {
    await this.dbReady;
    
    const entry = await this.getFromCache(cacheKey);
    if (entry) {
      entry.lastAccessed = Date.now();
      await this.storeInCache(cacheKey, entry.imageData, entry.isComplete);
    }
  }

  /**
   * Enforce cache limits (LRU eviction)
   */
  async enforceCacheLimits() {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('lastAccessed');
      
      const request = index.openCursor();
      const entries = [];
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          entries.push(cursor.value);
          cursor.continue();
        } else {
          // Sort by last accessed (oldest first)
          entries.sort((a, b) => a.lastAccessed - b.lastAccessed);
          
          let totalBytes = entries.reduce((sum, e) => sum + e.byteSize, 0);
          let itemCount = entries.length;
          
          // Remove oldest entries until within limits
          for (const entry of entries) {
            if (itemCount <= this.maxItems && totalBytes <= this.maxBytes) {
              break;
            }
            
            store.delete(entry.cacheKey);
            totalBytes -= entry.byteSize;
            itemCount--;
          }
          
          resolve();
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Clear entire cache
   */
  async clearCache() {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    await this.dbReady;
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      const request = store.getAll();
      request.onsuccess = () => {
        const entries = request.result;
        const totalBytes = entries.reduce((sum, e) => sum + e.byteSize, 0);
        resolve({
          itemCount: entries.length,
          totalBytes,
          totalMB: (totalBytes / 1024 / 1024).toFixed(2)
        });
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Export singleton instance
export default new RenderService();

